import { TypeChecker } from '@attain-apps/type-guards'

import type { ExpenseCategory } from '../expense-category'
import type { ProjectId } from '../project-id'

import type { WorkbookData } from './workbook-data'

/**
 * A BCH current-month expense balance for one project and expense category.
 *
 * These entries represent the BCH outline's balance formula:
 *
 * ```ts
 * currentMonthBalance =
 *   priorMonthEnd - priorMidMonth + currentMidMonth
 * ```
 *
 * Balances are grouped by `ProjectID` / `All Projects` and expense category.
 */
export interface CurrentMonthBalance {
  readonly projectId: ProjectId
  readonly expenseCategory: ExpenseCategory
  readonly amount: number
}
export namespace CurrentMonthBalance {
  export const isCurrentMonthBalance: TypeChecker<CurrentMonthBalance> =
    TypeChecker.Factory.object({
      amount: TypeChecker.isNumber,
      expenseCategory: TypeChecker.isString,
      projectId: TypeChecker.isString,
    })

  /**
   * Groups current-month balance rows by project for document-level lookups.
   */
  export function byProjectId(
    balances: readonly CurrentMonthBalance[],
  ): ReadonlyMap<ProjectId, readonly CurrentMonthBalance[]> {
    const rowsByProject = new Map<ProjectId, CurrentMonthBalance[]>()
    for (const balance of balances) {
      rowsByProject.set(balance.projectId, [
        ...(rowsByProject.get(balance.projectId) ?? []),
        balance,
      ])
    }
    return rowsByProject
  }

  /**
   * Calculates current-month balances from parsed BCH workbook data.
   */
  export function fromWorkbookData(
    data: Readonly<WorkbookData>,
  ): readonly CurrentMonthBalance[] {
    // First group by project, then by expense category, because the outline's
    // formula is calculated for each project/category cell.
    const amountsByProjectAndCategory = new Map<
      ProjectId,
      Map<ExpenseCategory, number>
    >()

    // Multipliers encode the outline formula:
    // Prior Month End - Prior Mid-Month + Current Mid-Month.
    for (const { multiplier, rows } of [
      { multiplier: 1, rows: data.justifications.priorMonthEnd },
      { multiplier: -1, rows: data.justifications.priorMidMonth },
      { multiplier: 1, rows: data.justifications.currentMidMonth },
    ] as const) {
      for (const row of rows) {
        // Missing project/category combinations are treated as zero by
        // initializing the bucket when the first contributing row appears.
        const amountsByCategory =
          amountsByProjectAndCategory.get(row.projectId) ??
          new Map<ExpenseCategory, number>()
        amountsByProjectAndCategory.set(row.projectId, amountsByCategory)
        amountsByCategory.set(
          row.expenseCategory,
          // Add the signed contribution from this worksheet to the same
          // project/category balance.
          (amountsByCategory.get(row.expenseCategory) ?? 0) +
            row.amount * multiplier,
        )
      }
    }

    // Sort for stable downstream output and easier debugging.
    return [...amountsByProjectAndCategory.entries()]
      .sort(([projectIdA], [projectIdB]) =>
        projectIdA.localeCompare(projectIdB),
      )
      .flatMap(([projectId, amountsByCategory]) =>
        [...amountsByCategory.entries()]
          .sort(([expenseCategoryA], [expenseCategoryB]) =>
            expenseCategoryA.localeCompare(expenseCategoryB),
          )
          .map(([expenseCategory, amount]) => ({
            amount,
            expenseCategory,
            projectId,
          })),
      )
  }
}
